import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiRequest, ApiError } from "./api";
import type { SessionUser, UserRole } from "./types";

interface AuthContextValue {
  user: SessionUser | null;
  sandbox: SandboxStatus;
  loading: boolean;
  entering: boolean;
  finishEntrance: () => void;
  login: (role: UserRole, email: string, password: string, rememberDevice: boolean, animateEntrance?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshSandbox: () => Promise<void>;
  enterSandbox: () => Promise<void>;
  exitSandbox: () => Promise<void>;
}

export interface SandboxStatus { enabled: boolean; allowed: boolean; active: boolean; blocked: boolean }
const emptySandbox: SandboxStatus = { enabled: false, allowed: false, active: false, blocked: false };

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [sandbox, setSandbox] = useState<SandboxStatus>(emptySandbox);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(false);
  const finishEntrance = useCallback(() => setEntering(false), []);

  const refreshSandbox = useCallback(async () => {
    const state = await apiRequest<SandboxStatus>("/api/sandbox/status");
    setSandbox(state);
    try { window.sessionStorage.setItem("carioprint_sandbox_active", state.active ? "true" : "false"); } catch { /* El modo no depende de este indicador visual. */ }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const response = await apiRequest<{ user: SessionUser }>("/api/auth/me");
      setUser(response.user);
      await refreshSandbox();
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error(error);
      }
      setUser(null);
      setSandbox(emptySandbox);
      try { window.sessionStorage.removeItem("carioprint_sandbox_active"); } catch { /* No afecta la sesión. */ }
    } finally {
      setLoading(false);
    }
  }, [refreshSandbox]);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const login = useCallback(async (role: UserRole, email: string, password: string, rememberDevice: boolean, animateEntrance = true) => {
    const response = await apiRequest<{ user: SessionUser }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ role, email, password, rememberDevice }),
    });
    await refreshSandbox();
    // Only a confirmed explicit login starts the presentation; session refreshes never do.
    setEntering(animateEntrance && !window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    setUser(response.user);
  }, [refreshSandbox]);

  const logout = useCallback(async () => {
    await apiRequest<void>("/api/auth/logout", { method: "POST" });
    setEntering(false);
    setUser(null);
    setSandbox(emptySandbox);
    try { window.sessionStorage.removeItem("carioprint_sandbox_active"); } catch { /* No afecta la sesión. */ }
  }, []);

  const enterSandbox = useCallback(async () => {
    await apiRequest("/api/sandbox/enter", { method: "POST" });
    window.location.reload();
  }, []);

  const exitSandbox = useCallback(async () => {
    await apiRequest("/api/sandbox/exit", { method: "POST" });
    window.location.reload();
  }, []);

  const value = useMemo(
    () => ({ user, sandbox, loading, entering, finishEntrance, login, logout, refreshSession, refreshSandbox, enterSandbox, exitSandbox }),
    [loading, entering, finishEntrance, login, logout, refreshSession, refreshSandbox, enterSandbox, exitSandbox, user, sandbox],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth debe usarse dentro de AuthProvider.");
  return value;
}
